import { AlertTriangle, FileBarChart, ReceiptText } from "lucide-react";
import { useEffect, useState } from "react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { ManagerProfitReport } from "@/components/ManagerProfitReport";


const formatCurrency = (value) => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(value || 0);

const today = new Date().toISOString().slice(0, 10);

export default function ReportsPage() {
  const { user } = useAuth();
  const isManager = user.role === "MANAGER";
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("all");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [report, setReport] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isManager) {
      return;
    }

    api.get("/stores")
      .then((response) => setStores(response.data))
      .catch((requestError) => setError(formatApiError(requestError)));
  }, [isManager]);

  const loadReport = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (!isManager && storeId !== "all") {
        params.store_id = storeId;
      }
      const response = await api.get("/reports/profit", { params });
      setReport(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsLoading(false);
    }
  };

  const loadTransactions = async () => {
    if (storeId === "all") {
      setError("Pilih satu toko untuk melihat transaksi terbaru.");
      return;
    }
    setError("");
    try {
      const response = await api.get(`/reports/stores/${storeId}/transactions`);
      setTransactions(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };

  if (isManager) {
    return <ManagerProfitReport />;
  }

  return (
    <main className="workspace" data-testid="reports-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">LAPORAN POS</p>
          <h1 data-testid="reports-page-title">Penjualan, HPP, dan laba</h1>
        </div>
      </section>
      <p className="page-note" data-testid="profit-term-note">
        Laba memakai nilai tabel labarugi dari POS. Biaya operasional belum dikurangkan.
      </p>
      {error && <p className="form-error" data-testid="reports-error" role="alert">{error}</p>}

      <section className="form-section" aria-labelledby="profit-filter-title">
        <div className="section-heading"><div><p className="eyebrow">FILTER</p><h2 id="profit-filter-title">Periode laporan</h2></div></div>
        <form className="grid-form" data-testid="profit-report-filter-form" onSubmit={loadReport}>
          {!isManager && (
            <label htmlFor="report-store">Toko
              <select data-testid="report-store-select" id="report-store" onChange={(event) => setStoreId(event.target.value)} value={storeId}>
                <option value="all">Gabungan toko yang diizinkan</option>
                {stores.map((store) => <option key={store.store_id} value={store.store_id}>{store.name}</option>)}
              </select>
            </label>
          )}
          <label htmlFor="report-date-from">Dari tanggal
            <input data-testid="report-date-from-input" id="report-date-from" onChange={(event) => setDateFrom(event.target.value)} required type="date" value={dateFrom} />
          </label>
          <label htmlFor="report-date-to">Sampai tanggal
            <input data-testid="report-date-to-input" id="report-date-to" onChange={(event) => setDateTo(event.target.value)} required type="date" value={dateTo} />
          </label>
          <button className="form-action" data-testid="profit-report-submit-button" disabled={isLoading} type="submit">
            <FileBarChart aria-hidden="true" size={17} /> {isLoading ? "Membaca…" : "Tampilkan laporan"}
          </button>
        </form>
      </section>

      {report && (
        <>
          {!report.is_complete && (
            <section className="incomplete-banner" data-testid="profit-incomplete-data-alert" role="alert">
              <AlertTriangle aria-hidden="true" size={20} />
              <p>Nilai hanya dari {report.online_store_count}/{report.expected_store_count} toko yang online.</p>
            </section>
          )}
          <section className="metric-grid" aria-label="Ringkasan laba rugi">
            <article className="metric-card" data-testid="report-sales-card"><span>Penjualan</span><strong data-testid="report-sales-value">{formatCurrency(report.total_sales)}</strong><small>Nilai POS</small></article>
            <article className="metric-card" data-testid="report-hpp-card"><span>HPP / Modal</span><strong data-testid="report-hpp-value">{formatCurrency(report.total_hpp)}</strong><small>Nilai POS</small></article>
            <article className="metric-card" data-testid="report-profit-card"><span>Laba</span><strong data-testid="report-profit-value">{formatCurrency(report.total_profit)}</strong><small>Belum termasuk biaya operasional</small></article>
          </section>
          {!isManager && (
            <section className="data-section" aria-labelledby="profit-comparison-title">
            <div className="section-heading"><div><p className="eyebrow">PERBANDINGAN</p><h2 id="profit-comparison-title">Per toko</h2></div></div>
            <div className="report-table" data-testid="profit-store-comparison-table">
              {report.stores.map((store) => (
                <article data-testid={`profit-store-${store.store_id}`} key={store.store_id}>
                  <strong>{store.name}</strong><span>{store.connection_status === "online" ? "ONLINE" : "OFFLINE"}</span><span>{formatCurrency(store.total_sales)}</span><span>{formatCurrency(store.total_hpp)}</span><strong>{formatCurrency(store.total_profit)}</strong>
                </article>
              ))}
            </div>
            </section>
          )}
        </>
      )}

      {!isManager && (
        <section className="data-section" aria-labelledby="transaction-report-title">
        <div className="section-heading">
          <div><p className="eyebrow">TRANSAKSI TERBARU</p><h2 id="transaction-report-title">Detail per toko</h2></div>
          <button className="icon-text-button secondary" data-testid="load-transactions-button" onClick={loadTransactions} type="button">
            <ReceiptText aria-hidden="true" size={16} /> Muat transaksi
          </button>
        </div>
        <div className="transaction-report-list" data-testid="transaction-report-list">
          {transactions.map((transaction) => (
            <article data-testid={`report-transaction-${transaction.transaction_code}`} key={transaction.transaction_code}>
              <span>{transaction.transaction_time} · {transaction.transaction_code}</span>
              <span>{transaction.operator} / {transaction.cashier}</span>
              <strong>{formatCurrency(transaction.total_sales)}</strong>
            </article>
          ))}
        </div>
        </section>
      )}
    </main>
  );
}